import React from "react";
import DailyMessagesDrawer from "./daily_messages";
import AnnouncementsDrawer from "./announcements_drawer";
import AssistantButton from "./assistant_button";
import AssistantMenu from "./assistant_menu";
import AssistantChatDrawer from "./assistant_chat_drawer";
import Utils from "../utils";

const WIDGET_POSITION_KEY = 'mrpop_widget_position';
const WIDGET_GAP = 20;
const DEFAULT_BUTTON_SIZE = 60;
const DEFAULT_MENU_WIDTH = 370;
const DEFAULT_WIDGET_OFFSET = { right: 20, bottom: 20 };

const { useState, useEffect, useRef, useCallback, useMemo } = React;

function extractChatError(response) {
  return response.json().then((payload) => {
    if (payload && Array.isArray(payload.errors) && payload.errors.length > 0) {
      return payload.errors[0];
    }
    return 'No pudimos enviar tu mensaje. Inténtalo nuevamente más tarde.';
  }).catch((error) => {
    console.error(error);
    return 'No pudimos enviar tu mensaje. Inténtalo nuevamente más tarde.';
  });
}

function loadStoredWidgetOffset() {
  try {
    const raw = window.localStorage.getItem(WIDGET_POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.right === 'number' && typeof parsed.bottom === 'number') {
      return parsed;
    }
  } catch (error) { }
  return null;
}

function useAssistantChatController({
  initialChats,
  recentChatId,
  enabled,
  channelName,
  payloadKey,
  messageType,
  createEndpoint,
  createPayloadKey,
  messageEndpointBuilder
}) {
  const [showChat, setShowChat] = useState(false);
  const [chats, setChats] = useState(initialChats);
  const [activeChatId, setActiveChatId] = useState(recentChatId || null);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);

  const subscriptionsRef = useRef({});
  const activeChatIdRef = useRef(activeChatId);
  const initialChatIdRef = useRef(recentChatId || null);

  useEffect(() => {
    activeChatIdRef.current = activeChatId;
  }, [activeChatId]);

  useEffect(() => {
    if (!enabled) {
      setShowChat(false);
    }
  }, [enabled]);

  const unreadCount = useMemo(() => {
    return chats.reduce((count, chat) => {
      const unread = chat.messages.filter((message) => !message.read && message.from !== 'user');
      return count + unread.length;
    }, 0);
  }, [chats]);

  const markMessagesAsRead = useCallback((messages) => {
    if (!messages.length) {
      return Promise.resolve();
    }
    const metaElement = document.querySelector('meta[name="csrf-token"]');
    const token = metaElement && metaElement.content;
    const payload = {
      messages: messages.map((message) => ({ id: message.id, type: messageType }))
    };

    return fetch('/staff/batch/read_receipts', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      body: JSON.stringify(payload)
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Failed to mark chat messages as read');
      }
      const messageIds = messages.map((message) => message.id);
      setChats((prevChats) => prevChats.map((chat) => {
        const updatedMessages = chat.messages.map((message) => {
          if (messageIds.includes(message.id)) {
            return { ...message, read: true };
          }
          return message;
        });
        return { ...chat, messages: updatedMessages };
      }));
    }).catch((err) => {
      console.error(err);
    });
  }, [messageType]);

  const sortChats = useCallback((list) => {
    return list.slice().sort((a, b) => {
      const aDate = a.last_message_at || a.created_at;
      const bDate = b.last_message_at || b.created_at;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return new Date(bDate) - new Date(aDate);
    });
  }, []);

  const upsertChat = useCallback((chatPayload) => {
    setChats((prevChats) => {
      const filtered = prevChats.filter((chat) => chat.id !== chatPayload.id);
      const updated = [chatPayload, ...filtered];
      return sortChats(updated);
    });
  }, [sortChats]);

  const handleChatEvent = useCallback((data) => {
    if (!data || !data[payloadKey]) return;
    const chatPayload = data[payloadKey];
    upsertChat(chatPayload);

    if (
      !activeChatIdRef.current &&
      initialChatIdRef.current &&
      data.type === 'initialized' &&
      chatPayload.id === initialChatIdRef.current
    ) {
      setActiveChatId(chatPayload.id);
      initialChatIdRef.current = null;
    }
  }, [payloadKey, upsertChat]);

  const subscribeToChat = useCallback((chatId) => {
    if (!chatId) return;
    if (subscriptionsRef.current[chatId]) return;
    const subscription = Utils.createSubscription(channelName, handleChatEvent, { id: chatId });
    subscriptionsRef.current[chatId] = subscription;
  }, [channelName, handleChatEvent]);

  useEffect(() => {
    initialChats.forEach((chat) => {
      subscribeToChat(chat.id);
    });

    return () => {
      Object.values(subscriptionsRef.current).forEach((subscription) => {
        subscription && subscription.unsubscribe();
      });
      subscriptionsRef.current = {};
    };
  }, [initialChats, subscribeToChat]);

  useEffect(() => {
    chats.forEach((chat) => {
      subscribeToChat(chat.id);
    });
  }, [chats, subscribeToChat]);

  const markActiveChatMessagesAsRead = useCallback(() => {
    if (!showChat) return;
    if (!activeChatId) return;
    const activeChat = chats.find((chat) => chat.id === activeChatId);
    if (!activeChat) return;
    const unreadMessages = activeChat.messages.filter((message) => !message.read && message.from !== 'user');
    if (!unreadMessages.length) return;
    markMessagesAsRead(unreadMessages);
  }, [showChat, activeChatId, chats, markMessagesAsRead]);

  useEffect(() => {
    markActiveChatMessagesAsRead();
  }, [markActiveChatMessagesAsRead]);

  const toggleChat = useCallback(() => {
    if (!enabled) return;
    setShowChat((prev) => {
      const next = !prev;
      if (!next) {
        setError(null);
      }
      return next;
    });
  }, [enabled]);

  const closeChat = useCallback(() => {
    setShowChat(false);
    setError(null);
  }, []);

  const startNewChat = useCallback(() => {
    if (!enabled) return;
    setActiveChatId(null);
    setShowChat(true);
    setError(null);
  }, [enabled]);

  const selectChat = useCallback((chatId) => {
    if (!enabled) return;
    setActiveChatId(chatId);
    setShowChat(true);
    setError(null);
  }, [enabled]);

  const sendMessage = useCallback((messageContent) => {
    if (!enabled) return Promise.resolve();
    if (!messageContent || isSending) return Promise.resolve();
    const content = messageContent.trim();
    if (!content) return Promise.resolve();

    setIsSending(true);
    setError(null);

    const metaElement = document.querySelector('meta[name="csrf-token"]');
    const token = metaElement && metaElement.content;

    if (!activeChatId) {
      const payload = {};
      payload[createPayloadKey] = { user_input: content };

      return fetch(createEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token
        },
        body: JSON.stringify(payload)
      }).then((response) => {
        if (!response.ok) {
          return extractChatError(response).then((errorMessage) => {
            setError(errorMessage);
            throw new Error(errorMessage);
          });
        }
        return response.json().then((responsePayload) => {
          setError(null);
          return responsePayload;
        });
      }).then((responsePayload) => {
        if (!responsePayload || !responsePayload[payloadKey]) return;
        const chatPayload = responsePayload[payloadKey];
        upsertChat(chatPayload);
        subscribeToChat(chatPayload.id);
        setActiveChatId(chatPayload.id);
        setShowChat(true);
      }).catch((err) => {
        console.error(err);
      }).finally(() => {
        setIsSending(false);
      });
    }

    return fetch(messageEndpointBuilder(activeChatId), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      body: JSON.stringify({ message: { content } })
    }).then((response) => {
      if (!response.ok) {
        return extractChatError(response).then((errorMessage) => {
          setError(errorMessage);
          throw new Error(errorMessage);
        });
      }
      setError(null);
      return null;
    }).catch((err) => {
      console.error(err);
    }).finally(() => {
      setIsSending(false);
    });
  }, [activeChatId, createEndpoint, createPayloadKey, enabled, isSending, messageEndpointBuilder, payloadKey, subscribeToChat, upsertChat]);

  const activeChat = activeChatId ? chats.find((chat) => chat.id === activeChatId) : null;

  return {
    showChat,
    toggleChat,
    closeChat,
    startNewChat,
    selectChat,
    sendMessage,
    chats,
    activeChat,
    activeChatId,
    isSending,
    error,
    unreadCount
  };
}

export default function AiAssistant(props) {
  const {
    dailyMessages: dailyMessagesProp,
    popitoUrl,
    announcements: announcementsProp,
    settings,
    isAdmin = false,
    supportChats: supportChatsProp = [],
    supportChatSettings = {},
    copilotChats: copilotChatsProp = [],
    copilotChatSettings = {}
  } = props;
  const {
    useDailyMessage,
    useSupportChat,
    useCopilotChat
  } = settings;

  const [dailyMessages, setDailyMessages] = useState(dailyMessagesProp);
  const [announcements, setAnnouncements] = useState(announcementsProp);
  const [showDailyMessage, setShowDailyMessage] = useState(false);
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const suppressButtonClickRef = useRef(false);
  const [widgetOffset, setWidgetOffset] = useState(() => loadStoredWidgetOffset() || DEFAULT_WIDGET_OFFSET);
  const [isDraggingWidget, setIsDraggingWidget] = useState(false);

  const clampWidgetOffset = useCallback((offset) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const buttonRect = buttonRef.current ? buttonRef.current.getBoundingClientRect() : { width: DEFAULT_BUTTON_SIZE, height: DEFAULT_BUTTON_SIZE };
    const menuRect = menuRef.current ? menuRef.current.getBoundingClientRect() : { width: DEFAULT_MENU_WIDTH, height: 0 };

    const maxRight = Math.max(0, Math.min(vw - buttonRect.width, vw - menuRect.width));
    const right = Math.min(Math.max(offset.right, 0), maxRight);

    const maxBottom = Math.max(0, Math.min(
      vh - buttonRect.height,
      vh - buttonRect.height - WIDGET_GAP - menuRect.height
    ));
    const bottom = Math.min(Math.max(offset.bottom, 0), maxBottom);

    return { right, bottom };
  }, []);

  const handleWidgetDragStart = useCallback((event) => {
    if (event.button !== 0) return;
    if (event.target.closest('.assistant-announcement-bubble, #assistant-menu-close')) return;

    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const startOffset = widgetOffset;
    let dragged = false;

    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (!dragged && Math.hypot(dx, dy) < 4) return;
      dragged = true;
      setIsDraggingWidget(true);
      setWidgetOffset(clampWidgetOffset({
        right: startOffset.right - dx,
        bottom: startOffset.bottom - dy
      }));
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (!dragged) return;
      setIsDraggingWidget(false);
      suppressButtonClickRef.current = true;
      setWidgetOffset((current) => {
        try {
          window.localStorage.setItem(WIDGET_POSITION_KEY, JSON.stringify(current));
        } catch (error) { }
        return current;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [widgetOffset, clampWidgetOffset]);

  const supportChat = useAssistantChatController({
    initialChats: supportChatsProp,
    recentChatId: supportChatSettings.recentChatId,
    enabled: useSupportChat,
    channelName: 'SupportChatChannel',
    payloadKey: 'support_chat',
    messageType: 'SupportChatMessage',
    createEndpoint: '/staff/support_chats',
    createPayloadKey: 'support_chat',
    messageEndpointBuilder: (chatId) => `/staff/support_chats/${chatId}/messages`
  });

  const copilotChat = useAssistantChatController({
    initialChats: copilotChatsProp,
    recentChatId: copilotChatSettings.recentChatId,
    enabled: useCopilotChat,
    channelName: 'CopilotChatChannel',
    payloadKey: 'copilot_chat',
    messageType: 'CopilotChatMessage',
    createEndpoint: '/staff/copilot_chats',
    createPayloadKey: 'copilot_chat',
    messageEndpointBuilder: (chatId) => `/staff/copilot_chats/${chatId}/messages`
  });

  const unreadDailyMessages = dailyMessages.filter((message) => !message.read);
  const unreadAnnouncements = announcements.filter((announcement) => !announcement.read);

  const markDailyMessagesAsRead = () => {
    const metaElement = document.querySelector('meta[name="csrf-token"]');
    const token = metaElement && metaElement.content;
    return fetch('/staff/batch/read_receipts', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      body: JSON.stringify({
        messages: unreadDailyMessages.map((m) => ({ id: m.id, type: 'MessageOfTheDay' }))
      })
    }).then((response) => {
      if (!response.ok) {
        console.log(response);
        throw new Error('Failed to mark motd as read');
      }
      setDailyMessages((prev) => prev.map((m) => ({ ...m, read: true })));
    });
  };

  const markAnnouncementsAsRead = () => {
    const metaElement = document.querySelector('meta[name="csrf-token"]');
    const token = metaElement && metaElement.content;
    return fetch('/staff/batch/read_receipts', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      body: JSON.stringify({
        messages: unreadAnnouncements.map((m) => ({ id: m.id, type: 'Announcement' }))
      })
    }).then((response) => {
      if (!response.ok) {
        console.log(response);
        throw new Error('Failed to mark announcement as read');
      }
      setAnnouncements((prev) => prev.map((m) => ({ ...m, read: true })));
    });
  };

  const toggleDailyMessage = () => {
    if (showDailyMessage) {
      if (isAdmin || unreadDailyMessages.length === 0) {
        setShowDailyMessage(false);
        return;
      }
      return markDailyMessagesAsRead()
        .then(() => setShowDailyMessage(false))
        .catch((err) => {
          console.error(err);
          setShowDailyMessage(false);
        });
    }

    setShowDailyMessage(true);
    return null;
  };

  const toggleAnnouncements = () => {
    if (showAnnouncements) {
      if (isAdmin || unreadAnnouncements.length === 0) {
        setShowAnnouncements(false);
        return;
      }
      return markAnnouncementsAsRead()
        .then(() => setShowAnnouncements(false))
        .catch((err) => {
          console.error(err);
          setShowAnnouncements(false);
        });
    }

    setShowAnnouncements(true);
    return null;
  };

  const openAnnouncementsDrawer = useCallback(() => {
    if (!showAnnouncements) {
      toggleAnnouncements();
    }
  }, [showAnnouncements, toggleAnnouncements]);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleAssistantButtonActivate = useCallback(() => {
    if (suppressButtonClickRef.current) {
      suppressButtonClickRef.current = false;
      return;
    }
    toggleMenu();
  }, [toggleMenu]);

  const widgetButtonStyle = { right: `${widgetOffset.right}px`, bottom: `${widgetOffset.bottom}px` };
  const widgetMenuStyle = {
    right: `${widgetOffset.right}px`,
    bottom: `${widgetOffset.bottom + DEFAULT_BUTTON_SIZE + WIDGET_GAP}px`
  };

  useEffect(() => {
    const handleResize = () => setWidgetOffset((current) => clampWidgetOffset(current));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampWidgetOffset]);

  useEffect(() => {
    let motdSubscription;
    const announcementSubscription = Utils.createSubscription('AnnouncementChannel', (data) => {
      if (data.announcement) {
        setAnnouncements((prev) => [...prev, data.announcement]);
      }
    });
    if (useDailyMessage) {
      motdSubscription = Utils.createSubscription('MessageOfTheDayChannel', (data) => {
        if (data.motd) {
          setDailyMessages((prev) => [...prev, data.motd]);
        }
      });
    }
    return () => {
      motdSubscription && motdSubscription.unsubscribe();
      announcementSubscription.unsubscribe();
    };
  }, [useDailyMessage]);

  const {
    showChat: showSupportChat,
    toggleChat: toggleSupportChatInternal,
    closeChat: closeSupportChat,
    startNewChat: startSupportNewChat,
    selectChat: selectSupportChat,
    sendMessage: sendSupportMessage,
    chats: supportChats,
    activeChat: activeSupportChat,
    isSending: isSendingSupportMessage,
    error: supportChatError,
    unreadCount: unreadSupportChatMessages
  } = supportChat;

  const {
    showChat: showCopilotChat,
    toggleChat: toggleCopilotChatInternal,
    closeChat: closeCopilotChat,
    startNewChat: startCopilotNewChat,
    selectChat: selectCopilotChat,
    sendMessage: sendCopilotMessage,
    chats: copilotChats,
    activeChat: activeCopilotChat,
    isSending: isSendingCopilotMessage,
    error: copilotChatError,
    unreadCount: unreadCopilotChatMessages
  } = copilotChat;

  const handleToggleSupportChat = useCallback(() => {
    if (!useSupportChat) return;
    if (!showSupportChat) {
      closeCopilotChat();
    }
    toggleSupportChatInternal();
  }, [useSupportChat, showSupportChat, closeCopilotChat, toggleSupportChatInternal]);

  const handleToggleCopilotChat = useCallback(() => {
    if (!useCopilotChat) return;
    if (!showCopilotChat) {
      closeSupportChat();
    }
    toggleCopilotChatInternal();
  }, [useCopilotChat, showCopilotChat, closeSupportChat, toggleCopilotChatInternal]);

  const closeAllChats = useCallback(() => {
    closeSupportChat();
    closeCopilotChat();
  }, [closeSupportChat, closeCopilotChat]);

  const showAnyChat = showSupportChat || showCopilotChat;

  let totalUnread = unreadAnnouncements.length;

  if (useDailyMessage) {
    totalUnread += unreadDailyMessages.length;
  }

  if (useSupportChat) {
    totalUnread += unreadSupportChatMessages;
  }

  if (useCopilotChat) {
    totalUnread += unreadCopilotChatMessages;
  }

  return (
    <div id="assistant-container">
      {showDailyMessage && (<div id="assistant-overlay" onClick={toggleDailyMessage}></div>)}
      {showAnnouncements && (<div id="assistant-overlay" onClick={toggleAnnouncements}></div>)}
      {showAnyChat && (<div id="assistant-overlay" onClick={closeAllChats}></div>)}
      {useDailyMessage && <DailyMessagesDrawer messages={dailyMessages} showDailyMessage={showDailyMessage} iconPath={popitoUrl} toggleDailyMessage={toggleDailyMessage} />}
      <AnnouncementsDrawer announcements={announcements} showAnnouncements={showAnnouncements} iconPath={popitoUrl} toggleAnnouncements={toggleAnnouncements} />
      {useSupportChat && (
        <AssistantChatDrawer
          drawerId="support-chat-drawer"
          headerTitle="Chat con Mr. Pop"
          showChat={showSupportChat}
          toggleChat={handleToggleSupportChat}
          iconPath={popitoUrl}
          chats={supportChats}
          activeChat={activeSupportChat}
          onSelectChat={selectSupportChat}
          onStartNewChat={startSupportNewChat}
          onSendMessage={sendSupportMessage}
          isSending={isSendingSupportMessage}
          statusLabels={supportChatSettings.statusLabels || {}}
          thinkingMessage={supportChatSettings.thinkingMessage || ''}
          errorMessage={supportChatError}
        />
      )}
      {useCopilotChat && (
        <AssistantChatDrawer
          drawerId="copilot-chat-drawer"
          headerTitle="Copilot"
          showChat={showCopilotChat}
          toggleChat={handleToggleCopilotChat}
          iconPath={popitoUrl}
          chats={copilotChats}
          activeChat={activeCopilotChat}
          onSelectChat={selectCopilotChat}
          onStartNewChat={startCopilotNewChat}
          onSendMessage={sendCopilotMessage}
          isSending={isSendingCopilotMessage}
          statusLabels={copilotChatSettings.statusLabels || {}}
          thinkingMessage={copilotChatSettings.thinkingMessage || ''}
          errorMessage={copilotChatError}
        />
      )}
      <AssistantMenu
        settings={settings}
        toggleMenu={toggleMenu}
        toggleAnnouncements={toggleAnnouncements}
        toggleDailyMessage={toggleDailyMessage}
        toggleCopilotChat={handleToggleCopilotChat}
        showMenu={showMenu}
        unreadAnnouncements={unreadAnnouncements.length}
        unreadDailyMessages={unreadDailyMessages.length}
        unreadCopilotChatMessages={unreadCopilotChatMessages}
        menuRef={menuRef}
        style={widgetMenuStyle}
        isDragging={isDraggingWidget}
        onDragStart={handleWidgetDragStart}
      />
      <AssistantButton
        popitoUrl={popitoUrl}
        toggleMenu={handleAssistantButtonActivate}
        showMenu={showMenu}
        totalUnread={totalUnread}
        hasUnreadAnnouncements={unreadAnnouncements.length > 0}
        onOpenAnnouncements={openAnnouncementsDrawer}
        buttonRef={buttonRef}
        style={widgetButtonStyle}
        isDragging={isDraggingWidget}
        onDragStart={handleWidgetDragStart}
      />
    </div>
  );
}
